import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getCanonicalUrl } from "../utils/seoConfig";
import Awards from "../Components/Awards";
import "../styles/landing.scss";

function About() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>{t("AboutPageTitle")}</title>
        <meta
          name="description"
          content={t("AboutPageDescription")}
        />
        <meta
          name="keywords"
          content={t("AboutPageKeywords")}
        />
        <link rel="canonical" href={getCanonicalUrl("/about")} />
      </Helmet>
      <div className="App">
        {/* Hero / Intro Section */}
        <section className="section about-hero">
          <div className="about-hero-content">
            <h1 className="about-hero-title">{t("AboutHeroTitle")}</h1>
            <p className="about-hero-subtitle">
              {t("AboutHeroSubtitle")}
            </p>
          </div>
        </section>

        {/* Our Story / Journey Section */}
        <section className="section journey">
          <div className="journey-wrapper">
            <div className="journey-header">
              <h2 className="section-title">{t("OurJourney")}</h2>
              <p className="section-subtitle">
                {t("FromBreakthroughToGlobalImpact")}
              </p>
            </div>

            <div className="journey-grid">
              <div className="journey-card" data-aos="fade-up">
                <div className="card-year">2024</div>
                <div className="card-content">
                  <div className="card-icon">
                    <svg className="journey-icon" viewBox="0 0 640 384">
                      <path d="M0 383.9l64 .0404c17.75 0 32-14.29 32-32.03V128.3L0 128.3V383.9zM48 320.1c8.75 0 16 7.118 16 15.99c0 8.742-7.25 15.99-16 15.99S32 344.8 32 336.1C32 327.2 39.25 320.1 48 320.1zM348.8 64c-7.941 0-15.66 2.969-21.52 8.328L228.9 162.3C228.8 162.5 228.8 162.7 228.6 162.7C212 178.3 212.3 203.2 226.5 218.7c12.75 13.1 39.38 17.62 56.13 2.75C282.8 221.3 282.9 221.3 283 221.2l79.88-73.1c6.5-5.871 16.75-5.496 22.62 1c6 6.496 5.5 16.62-1 22.62l-26.12 23.87L504 313.7c2.875 2.496 5.5 4.996 7.875 7.742V127.1c-40.98-40.96-96.48-63.88-154.4-63.88L348.8 64zM334.6 217.4l-30 27.49c-29.75 27.11-75.25 24.49-101.8-4.371C176 211.2 178.1 165.7 207.3 138.9L289.1 64H282.5C224.7 64 169.1 87.08 128.2 127.9L128 351.8l18.25 .0369l90.5 81.82c27.5 22.37 67.75 18.12 90-9.246l18.12 15.24c15.88 12.1 39.38 10.5 52.38-5.371l31.38-38.6l5.374 4.498c13.75 11 33.88 9.002 45-4.748l9.538-11.78c11.12-13.75 9.036-33.78-4.694-44.93L334.6 217.4zM544 128.4v223.6c0 17.62 14.25 32.05 31.1 32.05L640 384V128.1L544 128.4zM592 352c-8.75 0-16-7.246-16-15.99c0-8.875 7.25-15.99 16-15.99S608 327.2 608 336.1C608 344.8 600.8 352 592 352z" />
                    </svg>
                  </div>
                  <h3>{t("ProofOfConcept")}</h3>
                  <p>{t("ProofOfConceptDescription")}</p>
                </div>
              </div>

              <div
                className="journey-card active"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                <div className="card-year">2025</div>
                <div className="card-content">
                  <div className="card-icon">
                    <svg className="journey-icon" viewBox="0 0 576 512">
                      <path d="M32 64C32 46.33 46.33 32 64 32H128C145.7 32 160 46.33 160 64V215.4L316.6 131C332.6 122.4 352 134 352 152.2V215.4L508.6 131C524.6 122.4 544 134 544 152.2V432C544 458.5 522.5 480 496 480H80C53.49 480 32 458.5 32 432V64zM264 312C255.2 312 248 319.2 248 328V376C248 384.8 255.2 392 264 392H312C320.8 392 328 384.8 328 376V328C328 319.2 320.8 312 312 312H264zM120 376C120 384.8 127.2 392 136 392H184C192.8 392 200 384.8 200 376V328C200 319.2 192.8 312 184 312H136C127.2 312 120 319.2 120 328V376zM392 312C383.2 312 376 319.2 376 328V376C376 384.8 383.2 392 392 392H440C448.8 392 456 384.8 456 376V328C456 319.2 448.8 312 440 312H392z" />
                    </svg>
                  </div>
                  <h3>{t("MarketEntry")}</h3>
                  <p>{t("MarketEntryDescription")}</p>
                </div>
              </div>

              <div
                className="journey-card"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <div className="card-year">2027</div>
                <div className="card-content">
                  <div className="card-icon s-36">
                    <svg className="journey-icon" viewBox="0 0 512 512">
                      <path d="M352 256C352 278.2 350.8 299.6 348.7 320H163.3C161.2 299.6 159.1 278.2 159.1 256C159.1 233.8 161.2 212.4 163.3 192H348.7C350.8 212.4 352 233.8 352 256zM503.9 192C509.2 212.5 512 233.9 512 256C512 278.1 509.2 299.5 503.9 320H380.8C382.9 299.4 384 277.1 384 256C384 234 382.9 212.6 380.8 192H503.9zM493.4 160H376.7C366.7 96.14 346.9 42.62 321.4 8.442C399.8 29.09 463.4 85.94 493.4 160zM344.3 160H167.7C173.8 123.6 183.2 91.38 194.7 65.35C205.2 41.74 216.9 24.61 228.2 13.81C239.4 3.178 248.7 0 256 0C263.3 0 272.6 3.178 283.8 13.81C295.1 24.61 306.8 41.74 317.3 65.35C328.8 91.38 338.2 123.6 344.3 160H344.3zM18.61 160C48.59 85.94 112.2 29.09 190.6 8.442C165.1 42.62 145.3 96.14 135.3 160H18.61zM131.2 192C129.1 212.6 127.1 234 127.1 256C127.1 277.1 129.1 299.4 131.2 320H8.065C2.8 299.5 0 278.1 0 256C0 233.9 2.8 212.5 8.065 192H131.2zM194.7 446.6C183.2 420.6 173.8 388.4 167.7 352H344.3C338.2 388.4 328.8 420.6 317.3 446.6C306.8 470.3 295.1 487.4 283.8 498.2C272.6 508.8 263.3 512 255.1 512C248.7 512 239.4 508.8 228.2 498.2C216.9 487.4 205.2 470.3 194.7 446.6H194.7zM190.6 503.6C112.2 482.9 48.59 426.1 18.61 352H135.3C145.3 415.9 165.1 469.4 190.6 503.6V503.6zM321.4 503.6C346.9 469.4 366.7 415.9 376.7 352H493.4C463.4 426.1 399.8 482.9 321.4 503.6V503.6z" />
                    </svg>
                  </div>
                  <h3>{t("GlobalImpact")}</h3>
                  <p>{t("GlobalImpactDescription")}</p>
                </div>
              </div>
            </div>

            <Awards />
          </div>
        </section>

        {/* Problem We Solve Section */}
        <section className="section problem-we-solve">
          <div className="problem-we-solve-wrapper">
            <h2 className="section-title">{t("ProblemWeSolve")}</h2>
            <p className="section-subtitle">{t("ProblemWeSolveDesc")}</p>
            <div className="problem-grid">
              <div className="problem-item" data-aos="fade-up">
                <div className="problem-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 16L12 11L16 15L21 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>{t("ComplexTimeSeries")}</h3>
                <p>{t("ComplexTimeSeriesDesc")}</p>
              </div>
              <div className="problem-item" data-aos="fade-up" data-aos-delay="100">
                <div className="problem-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>{t("ProcessOptimization")}</h3>
                <p>{t("ProcessOptimizationDesc")}</p>
              </div>
              <div className="problem-item" data-aos="fade-up" data-aos-delay="200">
                <div className="problem-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>{t("AnomalyDetectionChallenge")}</h3>
                <p>{t("AnomalyDetectionChallengeDesc")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* How We Solve It Section */}
        <section className="section how-we-solve">
          <div className="how-we-solve-wrapper">
            <h2 className="section-title">{t("HowWeSolveIt")}</h2>
            <p className="section-subtitle">{t("HowWeSolveItDesc")}</p>
            <div className="solution-grid">
              <div className="solution-card" data-aos="fade-up">
                <div className="solution-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("NoCodeLabelingPlatform")}</h3>
                <p>{t("NoCodeLabelingPlatformDesc")}</p>
                <button 
                  className="secondary-button"
                  onClick={() => navigate("/products/labeling")}
                >
                  {t("LearnMore")} →
                </button>
              </div>
              <div className="solution-card" data-aos="fade-up" data-aos-delay="100">
                <div className="solution-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 16L12 11L16 15L21 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>{t("B2BAnomalyDetection")}</h3>
                <p>{t("B2BAnomalyDetectionDesc")}</p>
                <button 
                  className="secondary-button"
                  onClick={() => navigate("/products/anomaly-detection")}
                >
                  {t("LearnMore")} →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Team / Founders Section */}
        <section className="section team">
          <p className="title-p">{t("MeetOurTeam")}</p>
          <h1>
            {t("BuildingFuture")} <span>{t("IndustrialAI")}</span>
          </h1>

          <div className="team-container">
            <div className="team-item">
              <div className="team-item-inner">
                <div className="member-image" id="dali-image" />
                <div className="member-info">
                  <h3>Dalibor Mitic</h3>
                  <h3>CEO</h3>
                  <p>{t("AIAndManufacturingExpert")}</p>
                </div>
                <div className="member-info-overlay">
                  <p className="member-details">
                    {t("WithOver")} <strong>9+ {t("Years")}</strong>{" "}
                    {t("InPharmaceutical")} <strong>{t("Engineer")}</strong>{" "}
                    {t("SucLed")} <strong>{t("Project Manager")}</strong>
                    {". "}
                    {t("ProjectManager")} <strong>{t("AI/ML")}</strong>{" "}
                    {t("DuringStudies")}{" "}
                    <strong>{t("Domain Knowledge")}</strong>
                    {". "}
                  </p>
                </div>
              </div>
            </div>

            <div className="team-item">
              <div className="team-item-inner">
                <div className="member-image" id="ilija-image" />
                <div className="member-info">
                  <h3>Ilija Mikic</h3>
                  <h3>CTO</h3>
                  <p>{t("SoftwareEngineer")}</p>
                </div>
                <div className="member-info-overlay">
                  <p className="member-details">
                    {t("AversatileEngineer")}{" "}
                    <strong>{t("JavaPythonExpert")}</strong>{" "}
                    {t("WithExperience")} <strong>{t("Cybersecurity")}</strong>{" "}
                    <strong>{t("AndComputerVision")}</strong>.{" "}
                    {t("LeadsDevelopment")}
                  </p>
                </div>
              </div>
            </div>

            <div className="team-item">
              <div className="team-item-inner">
                <div className="member-image" id="milan-image" />
                <div className="member-info">
                  <h3>Milan Mitic</h3>
                  <h3>COO</h3>
                  <p>{t("SoftwareEngineer")}</p>
                </div>
                <div className="member-info-overlay">
                  <p className="member-details">
                    {t("A")} <strong>{t("FrontendDeveloper")}</strong>{" "}
                    {t("MilanExperience")} <strong>{t("UIDevelopment")}</strong>{" "}
                    {t("And")} <strong>{t("ERPConsulting")}</strong>
                    {". "}
                    {t("MilanSupport")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA / Contact Section */}
        <section className="section about-cta">
          <div className="about-cta-wrapper">
            <h2 className="section-title">{t("LearnMoreAboutOurProducts")}</h2>
            <p className="section-subtitle">{t("AboutCTADescription")}</p>
            <div className="about-cta-buttons">
              <button
                className="primary-button"
                onClick={() => navigate("/products/labeling")}
              >
                {t("ExploreProducts")}
              </button>
              <button
                className="secondary-button"
                onClick={() => {
                  navigate("/");
                  setTimeout(() => {
                    const contactSection = document.getElementById("contact");
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: "smooth" });
                    }
                  }, 100);
                }}
              >
                {t("Contact")}
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default About;

